import { Relationship } from './models/enums';

const format = /^\s*(\d{3})-?(\d{2})-?(\d{4})\s*$/;

export type RawSsn = string | null | undefined;

export function normalize(ssn: RawSsn): string | undefined {
  if (!ssn) return undefined;

  const matches = ssn.match(format);
  return matches ? matches[1] + matches[2] + matches[3] : undefined;
}

export class SsnDatabase {
  private countBySsn = new Map<string, number>();
  private copyOnWrite?: boolean;

  private ensureWritableStore() {
    if (this.copyOnWrite) {
      this.countBySsn = new Map(this.countBySsn);
      this.copyOnWrite = false;
    }
  }

  private getKey(ssn: RawSsn, relationshipCode: Relationship): string | undefined {
    const normalized = normalize(ssn);
    if (!normalized) return undefined;

    return `${normalized}|${relationshipCode}`;
  }

  register(ssn: RawSsn, relationshipCode: Relationship): boolean {
    const key = this.getKey(ssn, relationshipCode);
    if (!key) return false;

    this.ensureWritableStore();
    const count = this.countBySsn.get(key) ?? 0;
    this.countBySsn.set(key, count + 1);

    return true;
  }

  deregister(ssn: RawSsn, relationshipCode: Relationship): boolean {
    const key = this.getKey(ssn, relationshipCode);
    if (!key) return false;

    this.ensureWritableStore();
    const count = this.countBySsn.get(key);
    if (count) {
      if (count <= 1) this.countBySsn.delete(key);
      else this.countBySsn.set(key, count - 1);
    }
    return true;
  }

  isUnique(ssn: RawSsn, relationshipCode: Relationship): boolean | undefined {
    const key = this.getKey(ssn, relationshipCode);
    if (!key) return undefined;

    const count = this.countBySsn.get(key);
    return count == null || count <= 1;
  }

  clone(): SsnDatabase {
    const instance = new SsnDatabase();
    instance.countBySsn = this.countBySsn;
    instance.copyOnWrite = true;
    return instance;
  }
}
